import React from 'react';
import { Helmet } from 'react-helmet';

/**
 * StructuredData Component - Génère les données structurées Schema.org
 * pour améliorer le SEO et l'affichage dans les résultats de recherche
 */
const StructuredData = ({ type, data = {} }) => {
    let schema = null;

    switch (type) {
        case 'organization':
            schema = {
                '@context': 'https://schema.org',
                '@type': 'AutomotiveBusiness',
                '@id': 'https://www.sarayaauto.com/#organization',
                name: 'Saraya Auto Sales',
                legalName: 'Saraya Auto Sales',
                url: 'https://www.sarayaauto.com',
                logo: 'https://www.sarayaauto.com/saraya_logo.svg',
                description: 'Concessionnaire automobile spécialisé dans la vente de voitures d\'occasion de qualité à Dakar, Sénégal. Large sélection de véhicules fiables et certifiés.',
                telephone: '+221 77 415 65 65',
                email: 'contact@sarayaauto.com',
                address: {
                    '@type': 'PostalAddress',
                    streetAddress: 'Route de l\'aéroport, Yoff',
                    addressLocality: 'Dakar',
                    addressRegion: 'Dakar',
                    postalCode: '10700',
                    addressCountry: 'SN'
                },
                geo: {
                    '@type': 'GeoCoordinates',
                    latitude: 14.7645042,
                    longitude: -17.4957462
                },
                openingHoursSpecification: [
                    {
                        '@type': 'OpeningHoursSpecification',
                        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                        opens: '08:00',
                        closes: '19:00'
                    }
                ],
                sameAs: [
                    'https://www.facebook.com/sarayaautosales',
                    'https://www.instagram.com/sarayaautosales/',
                    'https://www.tiktok.com/@sarayaautosales',
                    'https://www.linkedin.com/company/saraya-auto-sales'
                ],
                priceRange: '$$',
                areaServed: {
                    '@type': 'Country',
                    name: 'Sénégal'
                }
            };
            break;

        case 'breadcrumb':
            schema = {
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: (data.items || []).map((item, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    name: item.name,
                    item: item.url
                }))
            };
            break;

        case 'product':
            schema = {
                '@context': 'https://schema.org',
                '@type': 'Car',
                name: data.name || `${data.year} ${data.make} ${data.model}`,
                description: data.description || `${data.year} ${data.make} ${data.model} - ${data.mileage} km - ${data.transmission} - ${data.fuelType}`,
                image: data.image || data.photos?.[0],
                brand: {
                    '@type': 'Brand',
                    name: data.make
                },
                model: data.model,
                vehicleModelDate: data.year?.toString(),
                mileageFromOdometer: {
                    '@type': 'QuantitativeValue',
                    value: data.mileage,
                    unitCode: 'KMT'
                },
                fuelType: data.fuelType,
                vehicleTransmission: data.transmission,
                color: data.color,
                numberOfDoors: data.doors,
                vehicleSeatingCapacity: data.seats,
                offers: {
                    '@type': 'Offer',
                    price: data.price,
                    priceCurrency: 'XOF',
                    availability: 'https://schema.org/InStock',
                    seller: {
                        '@type': 'AutomotiveBusiness',
                        name: 'Saraya Auto Sales',
                        '@id': 'https://www.sarayaauto.com/#organization'
                    },
                    url: data.url || `https://www.sarayaauto.com/cars/${data.id}`,
                    itemCondition: data.mileage === 0 ? 'https://schema.org/NewCondition' : 'https://schema.org/UsedCondition'
                }
            };
            break;

        case 'itemList':
            schema = {
                '@context': 'https://schema.org',
                '@type': 'ItemList',
                itemListElement: (data.items || []).map((item, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    url: item.url || `https://www.sarayaauto.com/cars/${item.id}`,
                    name: item.name || `${item.year} ${item.make} ${item.model}`
                }))
            };
            break;

        case 'website':
            schema = {
                '@context': 'https://schema.org',
                '@type': 'WebSite',
                '@id': 'https://www.sarayaauto.com/#website',
                url: 'https://www.sarayaauto.com',
                name: 'Saraya Auto Sales',
                description: 'Vente de voitures d\'occasion à Dakar - Concessionnaire automobile Sénégal',
                publisher: {
                    '@id': 'https://www.sarayaauto.com/#organization'
                },
                potentialAction: {
                    '@type': 'SearchAction',
                    target: {
                        '@type': 'EntryPoint',
                        urlTemplate: 'https://www.sarayaauto.com/?search={search_term_string}'
                    },
                    'query-input': 'required name=search_term_string'
                },
                inLanguage: ['fr-SN', 'en-US']
            };
            break;

        default:
            return null;
    }

    if (!schema) return null;

    return (
        <Helmet>
            <script type="application/ld+json">
                {JSON.stringify(schema)}
            </script>
        </Helmet>
    );
};

export default StructuredData;
